<script setup lang="ts">
import { ref } from 'vue'

import EditNoteForm from './EditNoteForm.vue'
import NotesHeader from './NotesHeader.vue'
import NotesList from './NotesList.vue'
import type { UserGender } from '@/types/app-data/user.types'

/** Base structure for a note */
interface NoteBase {
  title: string
  body: string
  last_edit?: {
    date: number
    user: {
      username: string
      firstname: string
      lastname: string
      email: string
    }
  }
}

/** Payload for saving/deleting a note */
interface NotePayload extends NoteBase {
  type: string
}

/** Note as received from props (with nested type) */
interface NoteType extends NoteBase {
  type: {
    name: string
  }
}

interface NotesOverviewProps {
  picture?: string
  vip: boolean
  highRisk: boolean
  gender: UserGender
  name: string
  notes: NoteType[]
}

withDefaults(
  defineProps<NotesOverviewProps>(),
  {
    picture: '',
    vip: false,
    highRisk: false,
    gender: 'other',
    name: '',
    notes: () => [],
  },
)

const emit = defineEmits<{
  (e: 'save-note', note: NotePayload): void
  (e: 'delete-note', note: NotePayload): void
  (e: 'should-close-without-saving'): void
}>()

const currentNote = ref<NotePayload | null>(null)

const createNewNote = () => {
  currentNote.value = { title: '', body: '', type: 'free' }
}

const closeOpenNote = () => {
  currentNote.value = null
}

const openNote = (note: NoteType) => {
  currentNote.value = { ...note, type: note.type.name }
}

const saveNote = () => {
  if (!currentNote.value) return
  emit('save-note', currentNote.value)
  closeOpenNote()
}

const deleteNote = (note: NotePayload) => {
  emit('delete-note', note)
}

const shouldCloseWithoutSaving = () => {
  emit('should-close-without-saving')
}

/**
 * Generic handler to update a field of the current note.
 * @param key The field name to update ('title' or 'body')
 * @param value The new value for the field
 */
const updateCurrentNoteField = (key: 'title' | 'body', value: string) => {
  if (!currentNote.value) return

  currentNote.value[key] = value
}

defineExpose({ closeOpenNote })
</script>

<template>
  <div class="notes-overview">
    <notes-header
      :picture="picture ?? ''"
      :gender="gender"
      :vip="vip"
      :high-risk="highRisk"
      :name="name"
      @close="shouldCloseWithoutSaving"
    />
    <div class="notes-overview__content">
      <edit-note-form
        v-if="currentNote"
        :title-value="currentNote.title"
        :body-value="currentNote.body"
        :last-edit="currentNote.last_edit"
        :note-type="currentNote.type"
        @title="value => updateCurrentNoteField('title', value)"
        @body="value => updateCurrentNoteField('body', value)"
        @cancel="closeOpenNote"
        @submit-form="saveNote"
      />
      <notes-list
        v-else
        :notes="notes"
        @open-note="openNote"
        @new-note="createNewNote"
        @delete-note="deleteNote"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notes-overview {
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-align: initial;
  box-shadow: 0 25px 40px 0px rgba(27, 27, 27, 0.2);

  &__content {
    height: calc(100% - 4.5rem);
  }
}

</style>
